/**
 * 画布基础类型。
 * 坐标约定：
 *  - 模型坐标（model）：无限平面，元素存储所用坐标系。
 *  - 屏幕坐标（screen）：相对画布容器左上角的像素坐标。
 * 转换由 Viewport 统一负责（借鉴 AFFiNE 的单一坐标真源设计）。
 */

export type IVec = [number, number];

export interface IBound {
  x: number;
  y: number;
  w: number;
  h: number;
}

export type ElementType =
  | 'note'
  | 'shape'
  | 'connector'
  | 'text'
  | 'frame'
  | 'group'
  // ── 侦探组件 ──
  | 'rope'      // 联系绳索（红线）
  | 'pin'       // 图钉
  | 'circle'    // 圈注
  | 'tape'      // 胶带
  | 'photo'     // 照片（宝丽来）
  | 'highlight' // 荧光标记
  | 'brush'     // 自由手绘
  | 'stamp'     // 印章
  | 'redact'; // 遮挡条

export type RefKind = 'target' | 'entity' | 'evidence' | 'hypothesis';

export type NoteColor =
  | 'default'
  | 'blue'
  | 'green'
  | 'yellow'
  | 'red'
  | 'purple'
  | 'orange';

export type NoteProps = {
  text: string;
  color: NoteColor;
  title?: string;
  refKind?: RefKind;
  refId?: string;
};

export type ShapeKind =
  | 'rect'
  | 'rounded'
  | 'ellipse'
  | 'diamond'
  | 'triangle'
  | 'hexagon'
  | 'star'
  | 'arrow';

export type ShapeProps = {
  shape: ShapeKind;
  fill: string;
  stroke: string;
  text: string;
  textColor: string;
};

export type ConnectorMode = 'straight' | 'orth';

/** 连接线端点：可连接元素（id + 0..1 归一化锚点）或自由坐标。 */
export type ConnectorEndpoint = {
  id?: string;
  anchor?: IVec;
  point?: IVec;
};

export type ConnectorProps = {
  source: ConnectorEndpoint;
  target: ConnectorEndpoint;
  mode: ConnectorMode;
  stroke: string;
  label?: string;
  dashed?: boolean;
};

export type TextProps = {
  text: string;
  color: string;
  fontSize: number;
  bold: boolean;
};

export type FrameProps = {
  title: string;
  color: string;
};

export type GroupProps = {
  children: string[];
};

/** 联系绳索：两点之间的悬垂曲线（红线）。 */
export type RopeProps = {
  source: ConnectorEndpoint;
  target: ConnectorEndpoint;
  color: string;
  sag: number;
  width: number;
  label?: string;
};

/** 图钉。 */
export type PinProps = {
  color: string;
  kind: 'push' | 'clip' | 'nail';
};

/** 圈注（手绘椭圆标注）。 */
export type CircleProps = {
  color: string;
  strokeWidth: number;
  dashed: boolean;
  text?: string;
  /** 稳定随机种子，保证抖动一致 */
  seed: number;
};

/** 胶带。 */
export type TapeProps = {
  color: string;
  seed: number;
};

/** 照片（宝丽来 / 裁剪 / 普通）。 */
export type PhotoProps = {
  src: string;
  caption: string;
  frame: 'polaroid' | 'plain' | 'clip';
};

/** 荧光标记。 */
export type HighlightProps = {
  color: string;
};

/** 遮挡条。 */
export type RedactProps = {
  color: string;
};

/** 自由手绘（铅笔/马克笔）。 */
export type BrushProps = {
  /** 相对元素左上角的模型坐标点列 */
  points: IVec[];
  color: string;
  width: number;
  /** pencil=铅笔（抖动）· marker=马克笔（透明叠色） */
  mode: 'pencil' | 'marker';
  seed: number;
};

/** 印章（证物章 / 密级章）。 */
export type StampProps = {
  text: string;
  color: string;
  shape: 'round' | 'rect';
  seed: number;
};

export type ElementProps =
  | NoteProps
  | ShapeProps
  | ConnectorProps
  | TextProps
  | FrameProps
  | GroupProps
  | RopeProps
  | PinProps
  | CircleProps
  | TapeProps
  | PhotoProps
  | HighlightProps
  | RedactProps
  | BrushProps
  | StampProps
  | Record<string, unknown>;

/** 画布元素统一结构。 */
export interface GfxElement<P = Record<string, unknown>> {
  id: string;
  type: ElementType;
  x: number;
  y: number;
  w: number;
  h: number;
  rotate: number;
  z: string;
  group: string | null;
  locked: boolean;
  hidden: boolean;
  /** 是否为仅本地元素（不入库），如拖拽预览、绘制中的连线。 */
  local: boolean;
  props: P;
  /** 关联的业务对象（目标/实体/证据/假设）。 */
  refKind?: RefKind;
  refId?: string;
  /** 宿主元素 id：随宿主一起移动/删除（图钉、胶带等）。 */
  attachTo?: string;
}

export function boundOf(el: GfxElement): IBound {
  return { x: el.x, y: el.y, w: el.w, h: el.h };
}

export function isConnector(el: GfxElement): boolean {
  return el.type === 'connector';
}

/** 连接类元素（连线 / 绳索）。 */
export function isLinkLike(el: GfxElement): boolean {
  return el.type === 'connector' || el.type === 'rope';
}

/** 装饰类元素（不入库交互、不参与框选优先）。 */
export function isDecoration(el: GfxElement): boolean {
  return [
    'pin',
    'tape',
    'highlight',
    'redact',
    'circle',
    'rope',
    'photo',
    'brush',
    'stamp',
  ].includes(el.type);
}

export function isGroupLike(el: GfxElement): boolean {
  return el.type === 'group' || el.type === 'frame';
}

/** 默认元素尺寸。 */
export const DEFAULT_SIZE: Record<ElementType, { w: number; h: number }> = {
  note: { w: 240, h: 160 },
  shape: { w: 180, h: 120 },
  text: { w: 200, h: 48 },
  frame: { w: 520, h: 360 },
  connector: { w: 0, h: 0 },
  group: { w: 0, h: 0 },
  rope: { w: 0, h: 0 },
  pin: { w: 26, h: 26 },
  circle: { w: 320, h: 200 },
  tape: { w: 120, h: 32 },
  photo: { w: 220, h: 200 },
  highlight: { w: 240, h: 48 },
  brush: { w: 200, h: 120 },
  stamp: { w: 130, h: 130 },
  redact: { w: 180, h: 26 },
};

/** 便签配色：白底索引卡 + 分类色标（侦探卷宗风格）。 */
export const NOTE_COLORS: Record<NoteColor, { bg: string; border: string; accent: string }> = {
  default: { bg: '#ffffff', border: '#e3e0d8', accent: '#6b7280' },
  blue: { bg: '#ffffff', border: '#e3e0d8', accent: '#2563eb' },
  green: { bg: '#ffffff', border: '#e3e0d8', accent: '#0f7a5a' },
  yellow: { bg: '#ffffff', border: '#e3e0d8', accent: '#a16207' },
  red: { bg: '#ffffff', border: '#e3e0d8', accent: '#b3261e' },
  purple: { bg: '#ffffff', border: '#e3e0d8', accent: '#6d28d9' },
  orange: { bg: '#ffffff', border: '#e3e0d8', accent: '#c2410c' },
};

/** 关系类型到颜色的映射（情报语义）。 */
export const RELATION_COLORS: Record<string, string> = {
  works_at: '#2563eb',
  owns: '#6d28d9',
  member_of: '#0369a1',
  communicates_with: '#0f7a5a',
  alias_of: '#a16207',
  located_at: '#0f766e',
  controls: '#b3261e',
  reports_to: '#4338ca',
  link: '#78716c',
};

/** 实体类型的中文标签（画布绘制不依赖图标字体）。 */
export const ENTITY_LABELS: Record<string, string> = {
  person: '个人',
  organization: '组织',
  org: '组织',
  email: '邮箱',
  phone: '电话',
  domain: '域名',
  ip: 'IP',
  social: '社交账号',
  wallet: '钱包',
  location: '位置',
  nameserver: '名称服务器',
  document: '文档',
  image: '图片',
  evidence: '证据',
  hypothesis: '研判',
};

export function labelFor(type: string): string {
  return ENTITY_LABELS[type] ?? type;
}

/** 侦探组件调色板。 */
export const ROPE_COLORS = ['#b3261e', '#8c1c13', '#1e3a5f', '#a16207', '#3f3f46'];
export const PIN_COLORS = ['#b3261e', '#1e3a5f', '#a16207', '#0f7a5a', '#6d28d9', '#78716c'];
export const TAPE_COLORS = ['#f3e7c9', '#e8e2d0', '#d9e6f2', '#e7d7e0'];
export const MARKER_COLORS = ['#fde047', '#a7f3d0', '#bfdbfe', '#fecdd3', '#e9d5ff'];
export const REDACT_COLORS = ['#111827', '#1f2937'];
export const BRUSH_COLORS = ['#b3261e', '#1e3a5f', '#111827', '#a16207', '#0f7a5a'];
export const STAMP_PRESETS = ['证物', '已核实', '可疑', '机密', '排除'];
export const STAMP_COLORS = ['#b3261e', '#1e3a5f', '#6d28d9'];

/** 侦探组件中文名。 */
export const DETECTIVE_LABELS: Record<string, string> = {
  rope: '联系绳索',
  pin: '图钉',
  circle: '圈注',
  tape: '胶带',
  photo: '照片',
  highlight: '荧光标记',
  brush: '手绘标记',
  stamp: '印章',
  redact: '遮挡条',
};